import { Alert, Chip, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"

export type Sstv2EvidenceSafetyBannerProps = {
  /**
   * The SL1 locker the DISPLAYED run actually drove, read off its record.
   * `undefined` for a run started before LOCKER-344 (the field did not exist),
   * or when no run has been started yet — rendered as unknown, never as the old
   * DEMO-MIGRATION constant.
   */
  sourceLocker?: string
  /**
   * SST v2 backend the DISPLAYED run actually drove, read off its record
   * (LOCKER-345). `undefined` when no run has been started yet — rendered as
   * unknown, never as the old hardcoded `ssdev` host.
   */
  host?: string
  /** SL2 tenant the run materializes the V1 fixture + V2 locker into. */
  targetTenantId: string
}

/**
 * Safety banner for the Synthetic Migration tab — what a run does to shared
 * infrastructure, plus the identifying chips.
 *
 * Both identifying chips render what **the backend recorded on the run** — the
 * SL1 locker (LOCKER-344) and the SST v2 host (LOCKER-345) — never a
 * compile-time constant. A chip that echoes a constant proves nothing about
 * what the run drove: the host one printed `ssdev` in every environment while
 * the requests resolved the real backend from SSM per call.
 *
 * Extracted from Sstv2SyntheticMigrationTab to keep that file under the
 * ~400-line component budget once the locker input landed.
 */
export const Sstv2EvidenceSafetyBanner: FC<Sstv2EvidenceSafetyBannerProps> = ({
  sourceLocker,
  host,
  targetTenantId,
}) => {
  const { t } = useTranslation()

  return (
    /* info (not amber) per house UI rule */
    <Alert data-ui="UI-SL2W-264" severity="info" icon="⚠️" sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: "0.86rem" }}>
        {t("evidenceSafetyBanner")}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
        <Chip
          size="small"
          variant="outlined"
          label={`host: ${host ?? t("evidenceHostUnknown")}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`sl1: ${sourceLocker ?? t("evidenceSourceLockerUnknown")}`}
        />
        <Chip size="small" variant="outlined" label="mode: no-pause" />
      </Stack>
      <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 1 }}>
        {t("evidenceMaterializesNote", { tenant: targetTenantId })}
      </Typography>
    </Alert>
  )
}

export default Sstv2EvidenceSafetyBanner
